<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import BillingAPI from 'dashboard/api/billing';
import ButtonV4 from 'next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { firstAvailableTab } from './helpers';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountId } = useAccount();
const alert = useAlert();
const currentUser = useMapGetter('getCurrentUser');

// State
const isLoading = ref(true);
const accountData = ref(null);
const subscription = ref(null);
const plan = ref(null);
const invoice = ref(null);
const gateways = ref({ asaas_available: false, stripe_available: false });
const isFirstActivation = ref(false);

// Payment state
const activeTab = ref('pix');
const isGeneratingInvoice = ref(false);
const isGeneratingPix = ref(false);
const isGeneratingBoleto = ref(false);
const isRedirectingStripe = ref(false);
const showPixQrCode = ref(false);
const pixCopied = ref(false);
const paymentConfirmed = ref(false);
const pollInterval = ref(null);

// Self-signup: dados de cobrança (CPF/CNPJ) antes da 1ª cobrança
const hasBillingInfo = ref(false);
// Default true = comportamento brasileiro. Se a chamada falhar, a tela erra
// para o lado que já funcionava, em vez de esconder o passo de todo mundo.
const backendNeedsBillingInfo = ref(true);
const isSavingBilling = ref(false);
const billingForm = ref({ cpfCnpj: '', name: '', phone: '' });

// Computed
const isAdmin = computed(() => {
  if (!currentUser.value?.accounts) return false;
  const acct = currentUser.value.accounts.find(a => a.id === accountId.value);
  return acct?.role === 'administrator';
});

const invoiceTotal = computed(() => {
  if (invoice.value?.total_amount) return invoice.value.total_amount;
  if (subscription.value?.effective_price)
    return subscription.value.effective_price;
  return 0;
});

const invoiceDueDate = computed(() => {
  if (!invoice.value?.due_date) return null;
  const dateStr = invoice.value.due_date.split('T')[0];
  return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: ptBR });
});

const hasPix = computed(() => !!invoice.value?.pix_qr_code);
const hasBoleto = computed(() => !!invoice.value?.boleto_line);
const hasInvoice = computed(() => !!invoice.value);
// Quem decide é o BACKEND (`needs_billing_info`), não esta tela. O passo de
// CPF/CNPJ existe só para criar o cliente no Asaas, que não aceita documento
// estrangeiro — inferir aqui "não tem cliente Asaas, logo falta o CPF" prendia
// a conta internacional num beco sem saída: as abas de pagamento sumiam
// (inclusive a do cartão) e o validador de 11/14 dígitos recusava o NIF
// português de 9. O país e o modo de cobrança vivem no backend; a decisão
// também.
const needsBillingInfo = computed(
  () =>
    backendNeedsBillingInfo.value && !hasInvoice.value && !hasBillingInfo.value
);

const formatCurrency = value => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

// Polling
const stopPolling = () => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value);
    pollInterval.value = null;
  }
};

const startPolling = () => {
  if (pollInterval.value) return;
  if (!invoice.value?.id) return;

  pollInterval.value = setInterval(async () => {
    try {
      const response = await BillingAPI.getPaymentStatus(invoice.value.id);
      if (
        response.data.status === 'paid' ||
        response.data.account_status === 'active'
      ) {
        paymentConfirmed.value = true;
        stopPolling();
        // Refresh user data and redirect after a short delay
        setTimeout(async () => {
          await store.dispatch('setUser');
          window.location.href = `/app/accounts/${accountId.value}/dashboard`;
        }, 2000);
      }
    } catch {
      // Silent fail on poll
    }
  }, 5000);
};

// Methods
const fetchData = async () => {
  isLoading.value = true;
  try {
    const response = await BillingAPI.getPaywallInfo();
    accountData.value = response.data.account;
    subscription.value = response.data.subscription;
    plan.value = response.data.plan;
    invoice.value = response.data.invoice;
    gateways.value = response.data.payment_gateways || {};
    // Só depois de saber quais gateways existem: abrir numa aba que não está
    // na barra deixa o cliente num painel que ele não consegue nem trocar.
    activeTab.value = firstAvailableTab(gateways.value);
    isFirstActivation.value = !!response.data.is_first_activation;
    hasBillingInfo.value = !!response.data.has_billing_info;
    backendNeedsBillingInfo.value = response.data.needs_billing_info !== false;
    if (!billingForm.value.name && response.data.account?.name) {
      billingForm.value.name = response.data.account.name;
    }
    // No cadastro pago o PIX já vem pronto da criação da conta: nenhum dos
    // botões que iniciavam o polling (generatePix/generateBoleto/
    // submitBillingInfo) chega a ser clicado. Sem isto, quem paga fica preso
    // no paywall se o webhook do Asaas atrasar ou falhar — nem recarregando
    // sai, porque o polling é quem confirma o pagamento no Asaas.
    if (invoice.value?.id && invoice.value.status !== 'paid') {
      startPolling();
    }
  } catch {
    // If the API fails, we still show the page
  } finally {
    isLoading.value = false;
  }
};

const ensureInvoice = async () => {
  if (hasInvoice.value) return true;
  isGeneratingInvoice.value = true;
  try {
    const response = await BillingAPI.generateInvoice();
    invoice.value = response.data.invoice;
    return true;
  } catch (error) {
    alert.show(
      error.response?.data?.error || t('BILLING.PAYWALL.ERROR_INVOICE'),
      'error'
    );
    return false;
  } finally {
    isGeneratingInvoice.value = false;
  }
};

const generatePix = async () => {
  if (!(await ensureInvoice())) return;
  if (hasPix.value) {
    showPixQrCode.value = true;
    return;
  }
  isGeneratingPix.value = true;
  try {
    const response = await BillingAPI.generatePixForInvoice(invoice.value.id);
    invoice.value = response.data.invoice;
    showPixQrCode.value = true;
    startPolling();
    alert.show(t('BILLING.PAYWALL.PIX.GENERATED'), 'success');
  } catch (error) {
    alert.show(
      error.response?.data?.error || t('BILLING.PAYWALL.PIX.ERROR'),
      'error'
    );
  } finally {
    isGeneratingPix.value = false;
  }
};

// Self-signup: salva CPF/CNPJ → cria cliente ASAAS → gera a 1ª fatura (mês
// cheio, sem pró-rata) + PIX, e já mostra o QR.
const submitBillingInfo = async () => {
  const doc = (billingForm.value.cpfCnpj || '').replace(/\D/g, '');
  if (doc.length !== 11 && doc.length !== 14) {
    alert.show(t('BILLING.PAYWALL.BILLING_INFO.INVALID_DOC'), 'error');
    return;
  }
  isSavingBilling.value = true;
  try {
    await BillingAPI.saveFirstPaymentInfo({
      cpfCnpj: doc,
      name: billingForm.value.name,
      phone: billingForm.value.phone,
    });
    const response = await BillingAPI.generateFirstPaymentInvoice();
    invoice.value = response.data.invoice;
    hasBillingInfo.value = true;
    gateways.value.asaas_available = true;
    activeTab.value = 'pix';
    showPixQrCode.value = true;
    startPolling();
  } catch (error) {
    alert.show(
      error.response?.data?.error || t('BILLING.PAYWALL.BILLING_INFO.ERROR'),
      'error'
    );
  } finally {
    isSavingBilling.value = false;
  }
};

const copyPixPayload = async () => {
  if (invoice.value?.pix_payload) {
    try {
      await navigator.clipboard.writeText(invoice.value.pix_payload);
      pixCopied.value = true;
      setTimeout(() => {
        pixCopied.value = false;
      }, 2000);
    } catch {
      // Fallback
    }
  }
};

const generateBoleto = async () => {
  if (!(await ensureInvoice())) return;
  isGeneratingBoleto.value = true;
  try {
    const response = await BillingAPI.generateBoletoForInvoice(
      invoice.value.id
    );
    invoice.value = response.data.invoice;
    startPolling();
    alert.show(t('BILLING.PAYWALL.BOLETO.GENERATED'), 'success');
  } catch (error) {
    alert.show(
      error.response?.data?.error || t('BILLING.PAYWALL.BOLETO.ERROR'),
      'error'
    );
  } finally {
    isGeneratingBoleto.value = false;
  }
};

const copyBoletoLine = async () => {
  if (invoice.value?.boleto_line) {
    try {
      await navigator.clipboard.writeText(invoice.value.boleto_line);
      alert.show(t('BILLING.PAYWALL.BOLETO.COPIED'), 'success');
    } catch {
      // Fallback
    }
  }
};

// Checkout de ASSINATURA, não pagamento avulso: o cliente autoriza uma vez e o
// Stripe cobra sozinho todo mês. Antes daqui saía `createCheckout`
// (`mode: 'payment'`), que cobrava o primeiro mês e nada mais — a conta 67
// pagou em 15/08 e não teria renovação nenhuma.
//
// A fatura continua sendo gerada antes: é ela que o webhook marca como paga,
// e é o `mark_as_paid!` que ativa a assinatura e inicia o ciclo.
const payWithStripe = async () => {
  if (!(await ensureInvoice())) return;
  isRedirectingStripe.value = true;
  try {
    const response = await BillingAPI.subscribeCard();
    if (response.data.checkout_url) {
      window.location.href = response.data.checkout_url;
    }
  } catch (error) {
    alert.show(
      error.response?.data?.error || t('BILLING.PAYWALL.STRIPE.ERROR'),
      'error'
    );
    isRedirectingStripe.value = false;
  }
};

const handleLogout = async () => {
  await store.dispatch('logout');
  router.push({ path: '/app/login' });
};

// Handle Stripe redirect back
const handleStripeRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const stripeResult = urlParams.get('stripe');
  if (stripeResult === 'success') {
    paymentConfirmed.value = true;
    alert.show(t('BILLING.PAYWALL.STATUS.CONFIRMED'), 'success');
    window.history.replaceState({}, '', window.location.pathname);
    setTimeout(async () => {
      await store.dispatch('setUser');
      window.location.href = `/app/accounts/${accountId.value}/dashboard`;
    }, 2000);
  }
};

onMounted(() => {
  handleStripeRedirect();
  fetchData();
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div class="min-h-screen bg-n-background flex flex-col">
    <!-- Header -->
    <div
      class="bg-n-solid-2 border-b border-n-weak px-6 py-4 flex items-center justify-between"
    >
      <div class="flex items-center gap-3">
        <span class="i-lucide-message-circle w-7 h-7 text-n-brand" />
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span class="text-lg font-semibold text-n-slate-12">ChatSuite</span>
      </div>
      <button
        type="button"
        class="text-sm text-n-slate-11 hover:text-n-red-11 transition-colors flex items-center gap-1.5"
        @click="handleLogout"
      >
        <span class="i-lucide-log-out w-4 h-4" />
        {{ $t('BILLING.PAYWALL.LOGOUT') }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <Spinner :size="32" />
    </div>

    <!-- Content -->
    <div v-else class="flex-1 flex items-center justify-center p-4">
      <div class="w-full max-w-lg space-y-6">
        <!-- Payment Confirmed Banner -->
        <div
          v-if="paymentConfirmed"
          class="p-4 rounded-xl bg-n-teal-3 border border-n-teal-6 text-center"
        >
          <span
            class="i-lucide-check-circle w-10 h-10 text-n-teal-11 mx-auto mb-2"
          />
          <p class="text-lg font-semibold text-n-teal-11">
            {{ $t('BILLING.PAYWALL.STATUS.CONFIRMED') }}
          </p>
          <p class="text-sm text-n-teal-11 mt-1">
            {{ $t('BILLING.PAYWALL.STATUS.REDIRECTING') }}
          </p>
        </div>

        <!-- ============ WELCOME (First Activation) ============ -->
        <template v-if="isFirstActivation && !paymentConfirmed">
          <!-- Welcome Hero -->
          <div class="text-center pt-2">
            <div
              class="w-16 h-16 rounded-2xl bg-n-brand/10 flex items-center justify-center mx-auto mb-4"
            >
              <span class="i-lucide-rocket w-8 h-8 text-n-brand" />
            </div>
            <h1 class="text-2xl font-bold text-n-slate-12 mb-2">
              {{ $t('BILLING.PAYWALL.WELCOME_TITLE') }}
            </h1>
            <p class="text-sm text-n-slate-11 leading-relaxed max-w-sm mx-auto">
              {{ $t('BILLING.PAYWALL.WELCOME_SUBTITLE') }}
            </p>
          </div>

          <!-- Feature Cards -->
          <div class="grid grid-cols-1 gap-3">
            <div
              class="flex items-start gap-3 p-4 rounded-xl bg-n-solid-2 border border-n-weak"
            >
              <div
                class="w-10 h-10 rounded-lg bg-n-brand/10 flex items-center justify-center shrink-0"
              >
                <span class="i-lucide-bot w-5 h-5 text-n-brand" />
              </div>
              <div>
                <p class="font-semibold text-n-slate-12 text-sm">
                  {{ $t('BILLING.PAYWALL.WELCOME_FEATURE_1_TITLE') }}
                </p>
                <p class="text-xs text-n-slate-11 mt-0.5">
                  {{ $t('BILLING.PAYWALL.WELCOME_FEATURE_1_DESC') }}
                </p>
              </div>
            </div>
            <div
              class="flex items-start gap-3 p-4 rounded-xl bg-n-solid-2 border border-n-weak"
            >
              <div
                class="w-10 h-10 rounded-lg bg-n-teal-3 flex items-center justify-center shrink-0"
              >
                <span class="i-lucide-messages-square w-5 h-5 text-n-teal-11" />
              </div>
              <div>
                <p class="font-semibold text-n-slate-12 text-sm">
                  {{ $t('BILLING.PAYWALL.WELCOME_FEATURE_2_TITLE') }}
                </p>
                <p class="text-xs text-n-slate-11 mt-0.5">
                  {{ $t('BILLING.PAYWALL.WELCOME_FEATURE_2_DESC') }}
                </p>
              </div>
            </div>
            <div
              class="flex items-start gap-3 p-4 rounded-xl bg-n-solid-2 border border-n-weak"
            >
              <div
                class="w-10 h-10 rounded-lg bg-n-amber-3 flex items-center justify-center shrink-0"
              >
                <span class="i-lucide-zap w-5 h-5 text-n-amber-11" />
              </div>
              <div>
                <p class="font-semibold text-n-slate-12 text-sm">
                  {{ $t('BILLING.PAYWALL.WELCOME_FEATURE_3_TITLE') }}
                </p>
                <p class="text-xs text-n-slate-11 mt-0.5">
                  {{ $t('BILLING.PAYWALL.WELCOME_FEATURE_3_DESC') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Non-admin message (welcome variant) -->
          <div
            v-if="!isAdmin"
            class="bg-n-solid-2 rounded-2xl border border-n-weak p-8 text-center"
          >
            <span class="i-lucide-clock w-12 h-12 text-n-brand mx-auto mb-4" />
            <p class="text-lg font-semibold text-n-slate-12 mb-2">
              {{ $t('BILLING.PAYWALL.NON_ADMIN.TITLE') }}
            </p>
            <p class="text-sm text-n-slate-11">
              {{ $t('BILLING.PAYWALL.NON_ADMIN.MESSAGE') }}
            </p>
          </div>

          <!-- Admin: Activation Payment -->
          <template v-if="isAdmin">
            <div
              class="bg-n-solid-2 rounded-2xl border border-n-brand/20 overflow-hidden"
            >
              <!-- Amount header (welcome style) -->
              <div class="p-6 border-b border-n-weak bg-n-brand/5">
                <div class="text-center">
                  <p class="text-sm text-n-slate-11 mb-1">
                    {{ $t('BILLING.PAYWALL.WELCOME_AMOUNT_LABEL') }}
                  </p>
                  <p class="text-3xl font-bold text-n-slate-12">
                    {{ formatCurrency(invoiceTotal) }}
                  </p>
                  <p v-if="plan" class="text-sm text-n-brand mt-1 font-medium">
                    {{ $t('BILLING.PAYWALL.PLAN') }}: {{ plan.name }}
                  </p>
                </div>
              </div>

              <!-- Self-signup: coleta CPF/CNPJ antes da 1ª cobrança -->
              <div v-if="needsBillingInfo" class="p-6 space-y-4">
                <p class="text-sm text-n-slate-11 text-center">
                  {{ $t('BILLING.PAYWALL.BILLING_INFO.HINT') }}
                </p>
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">
                    {{ $t('BILLING.PAYWALL.BILLING_INFO.DOCUMENT') }}
                  </label>
                  <input
                    v-model="billingForm.cpfCnpj"
                    type="text"
                    inputmode="numeric"
                    :placeholder="
                      $t('BILLING.PAYWALL.BILLING_INFO.DOCUMENT_PH')
                    "
                    class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">
                    {{ $t('BILLING.PAYWALL.BILLING_INFO.NAME') }}
                  </label>
                  <input
                    v-model="billingForm.name"
                    type="text"
                    class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">
                    {{ $t('BILLING.PAYWALL.BILLING_INFO.PHONE') }}
                  </label>
                  <input
                    v-model="billingForm.phone"
                    type="tel"
                    class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12"
                  />
                </div>
                <ButtonV4
                  solid
                  blue
                  class="w-full"
                  :is-loading="isSavingBilling"
                  @click="submitBillingInfo"
                >
                  {{ $t('BILLING.PAYWALL.BILLING_INFO.CONTINUE') }}
                </ButtonV4>
              </div>

              <!-- Payment Tabs -->
              <div v-if="!needsBillingInfo" class="flex border-b border-n-weak">
                <button
                  v-if="gateways.asaas_available"
                  type="button"
                  class="flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2"
                  :class="
                    activeTab === 'pix'
                      ? 'border-n-brand text-n-brand'
                      : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="activeTab = 'pix'"
                >
                  <span
                    class="i-lucide-qr-code w-4 h-4 mr-1.5 inline-block align-middle"
                  />
                  {{ $t('BILLING.PAYWALL.PIX.TITLE') }}
                </button>
                <button
                  v-if="gateways.asaas_available"
                  type="button"
                  class="flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2"
                  :class="
                    activeTab === 'boleto'
                      ? 'border-n-brand text-n-brand'
                      : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="activeTab = 'boleto'"
                >
                  <span
                    class="i-lucide-file-text w-4 h-4 mr-1.5 inline-block align-middle"
                  />
                  {{ $t('BILLING.PAYWALL.BOLETO.TITLE') }}
                </button>
                <button
                  v-if="gateways.stripe_available"
                  type="button"
                  class="flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2"
                  :class="
                    activeTab === 'stripe'
                      ? 'border-n-brand text-n-brand'
                      : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="activeTab = 'stripe'"
                >
                  <span
                    class="i-lucide-credit-card w-4 h-4 mr-1.5 inline-block align-middle"
                  />
                  {{ $t('BILLING.PAYWALL.STRIPE.TITLE') }}
                </button>
              </div>

              <div v-if="!needsBillingInfo" class="p-6">
                <!-- PIX Tab -->
                <div v-if="activeTab === 'pix'">
                  <ButtonV4
                    v-if="!hasPix"
                    solid
                    blue
                    class="w-full"
                    :is-loading="isGeneratingPix || isGeneratingInvoice"
                    @click="generatePix"
                  >
                    <span class="i-lucide-qr-code w-5 h-5 mr-2" />
                    {{ $t('BILLING.PAYWALL.WELCOME_CTA') }}
                  </ButtonV4>
                  <div v-if="hasPix" class="space-y-4">
                    <p class="text-sm text-n-slate-11 text-center">
                      {{ $t('BILLING.PAYWALL.PIX.SCAN_QR') }}
                    </p>
                    <div class="flex justify-center">
                      <div class="p-3 bg-white rounded-xl">
                        <img
                          :src="`data:image/png;base64,${invoice.pix_qr_code}`"
                          alt="PIX QR Code"
                          class="w-48 h-48"
                        />
                      </div>
                    </div>
                    <ButtonV4
                      faded
                      slate
                      class="w-full"
                      @click="copyPixPayload"
                    >
                      <span
                        :class="pixCopied ? 'i-lucide-check' : 'i-lucide-copy'"
                        class="w-4 h-4 mr-2"
                      />
                      {{
                        pixCopied
                          ? $t('BILLING.PAYWALL.PIX.COPIED')
                          : $t('BILLING.PAYWALL.PIX.COPY')
                      }}
                    </ButtonV4>
                  </div>
                </div>

                <!-- Boleto Tab -->
                <div v-if="activeTab === 'boleto'">
                  <ButtonV4
                    v-if="!hasBoleto"
                    solid
                    blue
                    class="w-full"
                    :is-loading="isGeneratingBoleto || isGeneratingInvoice"
                    @click="generateBoleto"
                  >
                    <span class="i-lucide-file-text w-5 h-5 mr-2" />
                    {{ $t('BILLING.PAYWALL.WELCOME_CTA') }}
                  </ButtonV4>
                  <div v-if="hasBoleto" class="space-y-4">
                    <div class="p-4 rounded-xl bg-n-alpha-2">
                      <p
                        class="text-xs text-n-slate-10 uppercase tracking-wide mb-2"
                      >
                        {{ $t('BILLING.PAYWALL.BOLETO.BARCODE') }}
                      </p>
                      <p class="text-sm text-n-slate-12 font-mono break-all">
                        {{ invoice.boleto_line }}
                      </p>
                    </div>
                    <div class="flex gap-3">
                      <ButtonV4
                        faded
                        slate
                        class="flex-1"
                        @click="copyBoletoLine"
                      >
                        <span class="i-lucide-copy w-4 h-4 mr-2" />
                        {{ $t('BILLING.PAYWALL.BOLETO.COPY_LINE') }}
                      </ButtonV4>
                      <a
                        v-if="invoice.boleto_url"
                        :href="invoice.boleto_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex-1"
                      >
                        <ButtonV4 faded slate class="w-full">
                          <span class="i-lucide-download w-4 h-4 mr-2" />
                          {{ $t('BILLING.PAYWALL.BOLETO.DOWNLOAD') }}
                        </ButtonV4>
                      </a>
                    </div>
                    <p class="text-xs text-n-slate-10 text-center">
                      {{ $t('BILLING.PAYWALL.BOLETO.PROCESSING_TIME') }}
                    </p>
                  </div>
                </div>

                <!-- Stripe Tab -->
                <div v-if="activeTab === 'stripe'">
                  <div class="text-center mb-4">
                    <span
                      class="i-lucide-credit-card w-10 h-10 text-n-slate-10 mx-auto mb-2"
                    />
                    <p class="text-sm text-n-slate-11">
                      {{ $t('BILLING.PAYWALL.STRIPE.REDIRECT_INFO') }}
                    </p>
                  </div>
                  <ButtonV4
                    solid
                    blue
                    class="w-full"
                    :is-loading="isRedirectingStripe || isGeneratingInvoice"
                    @click="payWithStripe"
                  >
                    <span class="i-lucide-external-link w-5 h-5 mr-2" />
                    {{ $t('BILLING.PAYWALL.WELCOME_CTA') }}
                  </ButtonV4>
                </div>
              </div>
            </div>

            <!-- Polling Status -->
            <div
              v-if="pollInterval"
              class="p-4 rounded-xl bg-n-amber-3 border border-n-amber-6 text-center"
            >
              <div class="flex items-center justify-center gap-2">
                <Spinner :size="16" />
                <p class="text-sm font-medium text-n-amber-11">
                  {{ $t('BILLING.PAYWALL.STATUS.WAITING') }}
                </p>
              </div>
            </div>
          </template>
        </template>

        <!-- ============ SUSPENDED (Returning blocked user) ============ -->
        <template v-if="!isFirstActivation && !paymentConfirmed">
          <!-- Alert Banner -->
          <div class="p-4 rounded-xl bg-n-red-3 border border-n-red-6">
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-alert-triangle w-6 h-6 text-n-red-11 shrink-0 mt-0.5"
              />
              <div>
                <p class="font-semibold text-n-red-11">
                  {{ $t('BILLING.PAYWALL.TITLE') }}
                </p>
                <p class="text-sm text-n-red-11 mt-1">
                  {{ $t('BILLING.PAYWALL.SUBTITLE') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Non-admin message -->
          <div
            v-if="!isAdmin"
            class="bg-n-solid-2 rounded-2xl border border-n-weak p-8 text-center"
          >
            <span
              class="i-lucide-lock w-12 h-12 text-n-slate-10 mx-auto mb-4"
            />
            <p class="text-lg font-semibold text-n-slate-12 mb-2">
              {{ $t('BILLING.PAYWALL.NON_ADMIN.TITLE') }}
            </p>
            <p class="text-sm text-n-slate-11">
              {{ $t('BILLING.PAYWALL.NON_ADMIN.MESSAGE') }}
            </p>
          </div>

          <!-- Admin Payment Section -->
          <template v-if="isAdmin">
            <div
              class="bg-n-solid-2 rounded-2xl border border-n-weak overflow-hidden"
            >
              <!-- Amount due header -->
              <div class="p-6 border-b border-n-weak">
                <div class="text-center">
                  <p class="text-sm text-n-slate-11 mb-1">
                    {{ $t('BILLING.PAYWALL.AMOUNT_DUE') }}
                  </p>
                  <p class="text-3xl font-bold text-n-slate-12">
                    {{ formatCurrency(invoiceTotal) }}
                  </p>
                  <p v-if="invoiceDueDate" class="text-sm text-n-red-11 mt-1">
                    {{ $t('BILLING.PAYWALL.DUE_DATE') }}: {{ invoiceDueDate }}
                  </p>
                </div>
              </div>

              <!-- Tabs -->
              <div class="flex border-b border-n-weak">
                <button
                  v-if="gateways.asaas_available"
                  type="button"
                  class="flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2"
                  :class="
                    activeTab === 'pix'
                      ? 'border-n-brand text-n-brand'
                      : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="activeTab = 'pix'"
                >
                  <span
                    class="i-lucide-qr-code w-4 h-4 mr-1.5 inline-block align-middle"
                  />
                  {{ $t('BILLING.PAYWALL.PIX.TITLE') }}
                </button>
                <button
                  v-if="gateways.asaas_available"
                  type="button"
                  class="flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2"
                  :class="
                    activeTab === 'boleto'
                      ? 'border-n-brand text-n-brand'
                      : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="activeTab = 'boleto'"
                >
                  <span
                    class="i-lucide-file-text w-4 h-4 mr-1.5 inline-block align-middle"
                  />
                  {{ $t('BILLING.PAYWALL.BOLETO.TITLE') }}
                </button>
                <button
                  v-if="gateways.stripe_available"
                  type="button"
                  class="flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2"
                  :class="
                    activeTab === 'stripe'
                      ? 'border-n-brand text-n-brand'
                      : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="activeTab = 'stripe'"
                >
                  <span
                    class="i-lucide-credit-card w-4 h-4 mr-1.5 inline-block align-middle"
                  />
                  {{ $t('BILLING.PAYWALL.STRIPE.TITLE') }}
                </button>
              </div>

              <div v-if="!needsBillingInfo" class="p-6">
                <!-- PIX Tab -->
                <div v-if="activeTab === 'pix'">
                  <ButtonV4
                    v-if="!hasPix"
                    solid
                    blue
                    class="w-full"
                    :is-loading="isGeneratingPix || isGeneratingInvoice"
                    @click="generatePix"
                  >
                    <span class="i-lucide-qr-code w-5 h-5 mr-2" />
                    {{ $t('BILLING.PAYWALL.PIX.GENERATE') }}
                  </ButtonV4>
                  <div v-if="hasPix" class="space-y-4">
                    <p class="text-sm text-n-slate-11 text-center">
                      {{ $t('BILLING.PAYWALL.PIX.SCAN_QR') }}
                    </p>
                    <div class="flex justify-center">
                      <div class="p-3 bg-white rounded-xl">
                        <img
                          :src="`data:image/png;base64,${invoice.pix_qr_code}`"
                          alt="PIX QR Code"
                          class="w-48 h-48"
                        />
                      </div>
                    </div>
                    <ButtonV4
                      faded
                      slate
                      class="w-full"
                      @click="copyPixPayload"
                    >
                      <span
                        :class="pixCopied ? 'i-lucide-check' : 'i-lucide-copy'"
                        class="w-4 h-4 mr-2"
                      />
                      {{
                        pixCopied
                          ? $t('BILLING.PAYWALL.PIX.COPIED')
                          : $t('BILLING.PAYWALL.PIX.COPY')
                      }}
                    </ButtonV4>
                  </div>
                </div>

                <!-- Boleto Tab -->
                <div v-if="activeTab === 'boleto'">
                  <ButtonV4
                    v-if="!hasBoleto"
                    solid
                    blue
                    class="w-full"
                    :is-loading="isGeneratingBoleto || isGeneratingInvoice"
                    @click="generateBoleto"
                  >
                    <span class="i-lucide-file-text w-5 h-5 mr-2" />
                    {{ $t('BILLING.PAYWALL.BOLETO.GENERATE') }}
                  </ButtonV4>
                  <div v-if="hasBoleto" class="space-y-4">
                    <div class="p-4 rounded-xl bg-n-alpha-2">
                      <p
                        class="text-xs text-n-slate-10 uppercase tracking-wide mb-2"
                      >
                        {{ $t('BILLING.PAYWALL.BOLETO.BARCODE') }}
                      </p>
                      <p class="text-sm text-n-slate-12 font-mono break-all">
                        {{ invoice.boleto_line }}
                      </p>
                    </div>
                    <div class="flex gap-3">
                      <ButtonV4
                        faded
                        slate
                        class="flex-1"
                        @click="copyBoletoLine"
                      >
                        <span class="i-lucide-copy w-4 h-4 mr-2" />
                        {{ $t('BILLING.PAYWALL.BOLETO.COPY_LINE') }}
                      </ButtonV4>
                      <a
                        v-if="invoice.boleto_url"
                        :href="invoice.boleto_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex-1"
                      >
                        <ButtonV4 faded slate class="w-full">
                          <span class="i-lucide-download w-4 h-4 mr-2" />
                          {{ $t('BILLING.PAYWALL.BOLETO.DOWNLOAD') }}
                        </ButtonV4>
                      </a>
                    </div>
                    <p class="text-xs text-n-slate-10 text-center">
                      {{ $t('BILLING.PAYWALL.BOLETO.PROCESSING_TIME') }}
                    </p>
                  </div>
                </div>

                <!-- Stripe Tab -->
                <div v-if="activeTab === 'stripe'">
                  <div class="text-center mb-4">
                    <span
                      class="i-lucide-credit-card w-10 h-10 text-n-slate-10 mx-auto mb-2"
                    />
                    <p class="text-sm text-n-slate-11">
                      {{ $t('BILLING.PAYWALL.STRIPE.REDIRECT_INFO') }}
                    </p>
                  </div>
                  <ButtonV4
                    solid
                    blue
                    class="w-full"
                    :is-loading="isRedirectingStripe || isGeneratingInvoice"
                    @click="payWithStripe"
                  >
                    <span class="i-lucide-external-link w-5 h-5 mr-2" />
                    {{ $t('BILLING.PAYWALL.STRIPE.PAY') }}
                  </ButtonV4>
                </div>
              </div>
            </div>

            <!-- Polling Status -->
            <div
              v-if="pollInterval"
              class="p-4 rounded-xl bg-n-amber-3 border border-n-amber-6 text-center"
            >
              <div class="flex items-center justify-center gap-2">
                <Spinner :size="16" />
                <p class="text-sm font-medium text-n-amber-11">
                  {{ $t('BILLING.PAYWALL.STATUS.WAITING') }}
                </p>
              </div>
            </div>
          </template>
        </template>

        <!-- Support -->
        <div class="text-center pb-4">
          <p class="text-xs text-n-slate-10">
            {{ $t('BILLING.PAYWALL.SUPPORT') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
